import * as React from "react";

import { ArrowLeft, Trash2 } from "lucide-react";

import type { EvidenceCardCitation } from "./evidence-card";

import { cn } from "../../utils";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "./alert-dialog";
import { Button } from "./button";
import { EvidenceCard, EvidenceCardFieldContext } from "./evidence-card";
import { Input } from "./input";
import { Label } from "./label";
import { Textarea } from "./textarea";

interface EvidenceCardFieldValue {
  title: string;
  note?: string;
}

interface EvidenceCardFieldProps extends Omit<
  React.HTMLAttributes<HTMLElement>,
  "value" | "onChange"
> {
  value?: EvidenceCardFieldValue;
  onChange?: (value: EvidenceCardFieldValue) => void;
  disabled?: boolean;
  pinned?: boolean;
  citations?: EvidenceCardCitation[];
  defaultEditing?: boolean;
  highlightedCitationId?: string;
  onDraftChange?: (value: EvidenceCardFieldValue) => void;
  onCancelEdit?: (initialCitations: EvidenceCardCitation[]) => void;
  onCitationClick?: (citation: EvidenceCardCitation) => void;
  onEditEnd?: () => void;
  onEditStart?: () => void;
  onRemoveCitation?: (citation: EvidenceCardCitation) => void;
  onTogglePin?: () => void;
}

const interactiveChildSelector = [
  "button",
  "a",
  "input",
  "textarea",
  "select",
  "summary",
  '[role="button"]',
  '[role="link"]',
  '[role="checkbox"]',
  '[role="switch"]',
  '[role="radio"]',
  '[role="menuitem"]',
  '[role="menuitemcheckbox"]',
  '[role="menuitemradio"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="textbox"]',
  '[role="searchbox"]',
  '[tabindex]:not([tabindex="-1"])',
  "[data-evidence-card-ignore-edit]",
].join(",");

function EvidenceCardField({
  value,
  onChange,
  disabled,
  pinned,
  citations,
  defaultEditing,
  highlightedCitationId,
  onDraftChange,
  onCancelEdit,
  onCitationClick,
  onEditEnd,
  onEditStart,
  onRemoveCitation,
  onTogglePin,
  className,
  children,
  onClick,
  onKeyDown,
  onKeyDownCapture,
  onPointerDown,
  role,
  tabIndex,
  "aria-disabled": ariaDisabled,
  "aria-describedby": ariaDescribedBy,
  ...props
}: EvidenceCardFieldProps) {
  const id = React.useId();
  const titleId = `${id}-title`;
  const noteId = `${id}-note`;
  const instructionsId = `${id}-instructions`;
  const cardRef = React.useRef<HTMLElement>(null);
  const editContainerRef = React.useRef<HTMLDivElement>(null);
  const titleInputRef = React.useRef<HTMLInputElement>(null);
  const shouldRestoreFocusRef = React.useRef(false);
  const [isEditing, setIsEditing] = React.useState(defaultEditing ?? false);
  const [editTitle, setEditTitle] = React.useState(value?.title ?? "");
  const [editNote, setEditNote] = React.useState(value?.note ?? "");
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false);
  const initialValuesRef = React.useRef({
    title: defaultEditing ? (value?.title ?? "") : "",
    note: defaultEditing ? (value?.note ?? "") : "",
    citations: defaultEditing ? (citations ?? []) : [],
    citationIds: defaultEditing
      ? (citations?.map((citation) => citation.id) ?? [])
      : [],
  });

  const hasExistingData = Boolean(value?.title);
  const citationIds = React.useMemo(
    () => citations?.map((citation) => citation.id) ?? [],
    [citations],
  );
  const hasChanges =
    editTitle.trim() !== initialValuesRef.current.title.trim() ||
    editNote.trim() !== initialValuesRef.current.note.trim() ||
    citationIds.join("|") !== initialValuesRef.current.citationIds.join("|");

  const resetDraftFromValue = React.useCallback(() => {
    const nextValue = {
      title: value?.title ?? "",
      note: value?.note ?? "",
      citations: citations ?? [],
      citationIds,
    };
    initialValuesRef.current = nextValue;
    setEditTitle(nextValue.title);
    setEditNote(nextValue.note);
  }, [citationIds, value?.note, value?.title]);

  const handleStartEdit = () => {
    if (disabled) return;
    resetDraftFromValue();
    setIsEditing(true);
    onEditStart?.();
  };

  const isInteractiveChild = (
    target: EventTarget,
    currentTarget: EventTarget,
  ) => {
    if (!(target instanceof Element) || target === currentTarget) return false;

    const interactiveChild = target.closest(interactiveChildSelector);
    return interactiveChild !== null && interactiveChild !== currentTarget;
  };

  const closeEditor = () => {
    shouldRestoreFocusRef.current = true;
    setIsEditing(false);
    onEditEnd?.();
  };

  const handleSave = () => {
    const trimmedTitle = editTitle.trim();
    if (!trimmedTitle) return;
    const trimmedNote = editNote.trim();
    onChange?.({
      title: trimmedTitle,
      note: trimmedNote ? trimmedNote : undefined,
    });
    closeEditor();
  };

  const handleCancelEdit = React.useCallback(() => {
    onCancelEdit?.(initialValuesRef.current.citations);
    resetDraftFromValue();
    if (!hasExistingData) {
      onChange?.({ title: "", note: undefined });
    }
    closeEditor();
  }, [hasExistingData, onCancelEdit, onChange, resetDraftFromValue]);

  const handleDelete = () => {
    onChange?.({ title: "", note: undefined });
    setShowDeleteConfirm(false);
    closeEditor();
  };

  React.useEffect(() => {
    if (defaultEditing) {
      onEditStart?.();
    }
  }, []);

  React.useEffect(() => {
    if (isEditing) {
      titleInputRef.current?.focus({ preventScroll: true });
      return;
    }

    if (!shouldRestoreFocusRef.current) return;

    shouldRestoreFocusRef.current = false;
    cardRef.current?.focus();
  }, [isEditing]);

  React.useEffect(() => {
    const editContainer = editContainerRef.current;
    if (!isEditing || !editContainer) return;

    const handleCancel = () => handleCancelEdit();
    editContainer.addEventListener("evidence-card-field-cancel", handleCancel);

    return () => {
      editContainer.removeEventListener(
        "evidence-card-field-cancel",
        handleCancel,
      );
    };
  }, [isEditing, handleCancelEdit]);

  if (isEditing) {
    return (
      <>
        <div
          {...props}
          ref={editContainerRef}
          data-evidence-card-field-editing=""
          className={cn(
            "af-flex af-flex-col af-gap-4 af-rounded-md af-border af-border-primary af-bg-card af-p-4",
            className,
          )}
          onKeyDown={onKeyDown}
          onKeyDownCapture={(e) => {
            onKeyDownCapture?.(e);
            if (e.defaultPrevented) return;

            if (e.key === "Escape") {
              e.stopPropagation();
              e.preventDefault();
              handleCancelEdit();
            }
          }}
        >
          <div className="af-flex af-flex-col af-gap-1.5">
            <Label htmlFor={titleId}>Evidence Title</Label>
            <Input
              ref={titleInputRef}
              id={titleId}
              value={editTitle}
              onChange={(e) => {
                const title = e.target.value;
                setEditTitle(title);
                onDraftChange?.({ title, note: editNote });
              }}
              placeholder="Enter Title"
            />
          </div>

          <div className="af-flex af-flex-col af-gap-1.5">
            <Label htmlFor={noteId}>
              Note{" "}
              <span className="af-font-normal af-text-muted-foreground">
                (Optional)
              </span>
            </Label>
            <Textarea
              id={noteId}
              value={editNote}
              onChange={(e) => {
                const note = e.target.value;
                setEditNote(note);
                onDraftChange?.({ title: editTitle, note });
              }}
              placeholder="Write additional notes"
              rows={3}
            />
          </div>

          <EvidenceCard.Citations
            citations={citations}
            highlightedCitationId={highlightedCitationId}
            onCitationClick={onCitationClick}
            onRemoveCitation={onRemoveCitation}
          />

          <p className="af-flex af-items-center af-gap-1 af-text-xs af-font-medium af-text-muted-foreground">
            <ArrowLeft className="af-size-3.5" />
            Select text in the document to add supporting evidence.
          </p>

          <div className="af-flex af-items-center af-justify-between">
            <div className="af-flex af-items-center af-gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowDeleteConfirm(true)}
                disabled={disabled}
                className="af-gap-1.5"
              >
                <Trash2 className="af-size-4" />
                Delete Evidence
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleCancelEdit}
                disabled={disabled}
              >
                Cancel Changes
              </Button>
            </div>
            <div className="af-flex af-items-center af-gap-2">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                disabled={!editTitle.trim() || disabled === true || !hasChanges}
                onClick={handleSave}
              >
                Done
              </Button>
            </div>
          </div>
        </div>

        <AlertDialog
          open={showDeleteConfirm}
          onOpenChange={setShowDeleteConfirm}
        >
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Remove evidence?</AlertDialogTitle>
              <AlertDialogDescription>
                This will permanently remove this evidence item. You cannot undo
                this action.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel variant="outline">Cancel</AlertDialogCancel>
              <AlertDialogAction variant="destructive" onClick={handleDelete}>
                Remove
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </>
    );
  }

  const cardChildren = children ?? (
    <>
      {value?.title && (
        <EvidenceCard.Header>
          <EvidenceCard.Title>{value.title}</EvidenceCard.Title>
          {onTogglePin && (
            <EvidenceCard.PinButton pinned={pinned} onClick={onTogglePin} />
          )}
        </EvidenceCard.Header>
      )}
      {value?.note && <EvidenceCard.Note>{value.note}</EvidenceCard.Note>}
      <EvidenceCard.Citations
        citations={citations}
        highlightedCitationId={highlightedCitationId}
        onCitationClick={onCitationClick}
      />
    </>
  );

  if (!cardChildren) {
    return null;
  }

  return (
    <EvidenceCardFieldContext.Provider value={{ disabled }}>
      <EvidenceCard
        {...props}
        ref={cardRef}
        pinned={pinned}
        role={role ?? "button"}
        tabIndex={disabled ? -1 : (tabIndex ?? 0)}
        aria-disabled={disabled === true ? true : ariaDisabled}
        aria-describedby={ariaDescribedBy ?? instructionsId}
        onPointerDown={(e) => {
          onPointerDown?.(e);
          if (e.defaultPrevented || e.button !== 0) return;
          if (isInteractiveChild(e.target, e.currentTarget)) return;

          e.preventDefault();
          handleStartEdit();
        }}
        onClick={onClick}
        onKeyDown={(e) => {
          onKeyDown?.(e);
          if (e.defaultPrevented || e.currentTarget !== e.target) return;

          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleStartEdit();
          }
        }}
        onKeyDownCapture={onKeyDownCapture}
        className={cn(
          "af-relative af-cursor-pointer focus-visible:af-outline-none focus-visible:af-ring-2 focus-visible:af-ring-ring focus-visible:af-ring-offset-2",
          disabled &&
            "af-cursor-not-allowed af-opacity-50 hover:af-bg-interface",
          className,
        )}
      >
        <span id={instructionsId} className="af-sr-only">
          Press Enter or Space to edit evidence.
        </span>
        {cardChildren}
      </EvidenceCard>
    </EvidenceCardFieldContext.Provider>
  );
}

EvidenceCardField.displayName = "EvidenceCardField";

export { EvidenceCardField };
export type { EvidenceCardFieldValue, EvidenceCardFieldProps };
